<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { crossfade, fade } from 'svelte/transition';
  import { AppState } from '../etc/stores';
  import Page from '../components/misc/Page.svelte';
  import HeaderInternal from '../components/misc/HeaderInternal.svelte';
  import Tracking from '../components/misc/Tracking.svelte';

  import ReferringStory from '../components/icons/ReferringStory.svelte';
  import ReferredStory from '../components/icons/ReferredStory.svelte';
  import ArrowStory from '../components/icons/ArrowStory.svelte';
  import Twitter from '../components/icons/Twitter.svelte';
  import Facebook from '../components/icons/Facebook.svelte';
  import Share from '../components/icons/Share.svelte';
  import ShareEmail from '../components/icons/ShareEmail.svelte';
  import CopyClipboard from '../components/icons/CopyClipboard.svelte';
  import ShareCheck from '../components/icons/ShareCheck.svelte';

  import type { IBusinessReferralDiscountType } from '../services/referral-program';
  import {
    copyTextToClipboard,
    formatMoney,
    getReferralProgramType,
    isMobile,
    pluralizeI18n
  } from '../etc/utils';
  import { useShare } from '../etc/hooks/useShare';
  import Heading from '../components/typography/Heading.svelte';
  import Text from '../components/typography/Text.svelte';
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { useFinishSignIn } from '../etc/hooks/useModalInternal';
  import { Auth } from '../services/auth';
  import { Container } from '../services/container';
  import { replaceVariables } from '../i18n';
  import ReferredNotEarnStory from '../components/icons/ReferredNotEarnStory.svelte';
  import Analytics from '../services/analytics';

  const auth = Container.get(Auth);

  const { referralProgram, referralLink } = $AppState;

  const location = useLocation();
  const navigate = useNavigate();

  const { open, close } = useFinishSignIn();

  const { twitterShare, facebookShare, emailShare, nativeShare } = useShare();

  const category = 'referral-program-state';

  let copied = false;

  let containerScroll;

  let handleNavigationBackPage;
  let timeoutAnimation;
  let startAnimation = false;

  const navAnimation: boolean = $location.state?.navAnimation;

  const [send, receive] = crossfade({
    duration: 150,
    fallback: fade
  });

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`referral.${text}`), values);
  };

  const getFormattedDiscountByDiscountType = (
    discount: string,
    discountType: IBusinessReferralDiscountType
  ) => {
    switch (discountType) {
      case 'coins':
        return t(pluralizeI18n('discount-coins', discount), {
          discount: Number(discount)
        });
      case 'money':
        return t('discount-money', {
          discount: formatMoney($AppState.business.location, discount)
        });
      case 'percentage':
      default:
        return t('discount-percentage', { discount: Number(discount) });
    }
  };

  const onBack = () => {
    close();
    if (navAnimation) {
      clearTimeout(timeoutAnimation);
      startAnimation = true;
      timeoutAnimation = setTimeout(() => {
        if (handleNavigationBackPage) handleNavigationBackPage();
        navigate(-1);
      }, 600);
    } else {
      navigate(-1);
    }
  };

  const onTrackingOpen = () => {
    if (containerScroll) {
      setTimeout(() => {
        containerScroll.scrollTo({
          top: containerScroll.scrollHeight,
          behavior: 'smooth'
        });
      }, 300);
    }
  };

  const programType = getReferralProgramType(referralProgram);

  const shareTwitter = () => {
    Analytics.logEvent('click-share-twitter', category);
    twitterShare({ url: referralLink.link, via: 'twismcoins' });
  };

  const shareFacebook = () => {
    Analytics.logEvent('click-share-facebook', category);
    facebookShare(referralLink.link);
  };

  const shareEmail = () => {
    Analytics.logEvent('click-share-email', category);
    emailShare({ body: referralLink.link });
  };

  const shareNative = () => {
    Analytics.logEvent('click-share-native', category);
    nativeShare({ url: referralLink.link });
  };

  const copyLink = () => {
    Analytics.logEvent('click-copy-link', category);
    copyTextToClipboard(referralLink.link);

    copied = true;
    setTimeout(() => {
      copied = false;
    }, 3800);
  };

  const referredReward = getFormattedDiscountByDiscountType(
    referralProgram.referredDiscount,
    referralProgram.referredDiscountType
  );
  const referringReward = getFormattedDiscountByDiscountType(
    referralProgram.referringDiscount,
    referralProgram.referringDiscountType
  );

  onMount(async () => {
    if (await auth.isSoftLogin()) {
      open();
    }
    Analytics.logPageView('Referral Program', '/referral-program');
  });

  onDestroy(() => {
    clearTimeout(timeoutAnimation);
  });
</script>

<HeaderInternal
  title={t('header')}
  {onBack}
  {navAnimation}
  {startAnimation}
  bind:handleNavigationBackPage
/>
<Page padding="0" hideCloseButton headerInternal>
  <div
    class="twism-referral"
    class:--animation={startAnimation}
    bind:this={containerScroll}
    in:fade={{ duration: navAnimation ? 200 : 0 }}
  >
    <div class="twism-referral__wrapper">
      <div class="twism-how-to">
        <div class="twism-row twism-how-to-title">
          <img
            src={$AppState.business.logo.publicPath.small}
            alt="Business Logo"
          />
          <Heading type="h3">
            {t(`how-to-title.${programType}`, {
              businessName: $AppState.business.name
            })}
          </Heading>
        </div>
        <div class="twism-row twism-how-to-referring">
          <div class="twism-how-to-icon">
            {#if programType === 'only-referring'}
              <ReferredNotEarnStory />
            {:else}
              <ReferringStory />
            {/if}
          </div>
          <Text component="p" type="medium">
            {@html t(`how-to-referring.${programType}`, {
              referredReward,
              referringReward
            })}
          </Text>
        </div>
        <ArrowStory />
        <div class="twism-row twism-how-to-referred">
          <div class="twism-how-to-icon">
            <ReferredStory />
          </div>
          <Text component="p" type="medium">
            {@html t(`how-to-referred.${programType}`, {
              referredReward,
              referringReward
            })}
          </Text>
        </div>
      </div>
      <div class="twism-share">
        <Heading type="h3" component="h3">{t('share-title')}</Heading>
        <div class="twism-share-items">
          <div class="twism-share-item" on:click={shareTwitter}>
            <div class="twism-share-icon">
              <Twitter />
            </div>
            <Text component="p" type="small">{t('share-methods.twitter')}</Text>
          </div>
          <div class="twism-share-item" on:click={shareFacebook}>
            <div class="twism-share-icon">
              <Facebook />
            </div>
            <Text component="p" type="small">
              {t('share-methods.facebook')}
            </Text>
          </div>
          {#if isMobile()}
            <div class="twism-share-item" on:click={shareNative}>
              <div class="twism-share-icon">
                <Share />
              </div>
              <Text component="p" type="small">{t('share-methods.send')}</Text>
            </div>
          {:else}
            <div class="twism-share-item" on:click={shareEmail}>
              <div class="twism-share-icon">
                <ShareEmail />
              </div>
              <Text component="p" type="small">{t('share-methods.email')}</Text>
            </div>
          {/if}
          <div
            class="twism-share-item"
            class:twism-copied={copied}
            on:click={copyLink}
          >
            <div class="twism-share-icon">
              {#if copied}
                <div in:send={{ key: 1 }} out:receive={{ key: 1 }}>
                  <ShareCheck />
                </div>
              {:else}
                <div out:receive={{ key: 2 }}>
                  <CopyClipboard />
                </div>
              {/if}
            </div>
            <div class="twism-share-item-copy">
              <Text component="p" type="small" textAlign="center">
                {copied ? t('share-copied') : t('share-methods.copy')}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Tracking on:open={onTrackingOpen} {programType} {referralLink} />
  </div>
</Page>

<style lang="scss">
  .twism-referral {
    padding-top: 24px;
    margin-bottom: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    transition: opacity 0.3s ease-in-out;
    &.--animation {
      opacity: 0;
    }

    &::-webkit-scrollbar {
      display: none;
    }

    &__wrapper {
      padding: 0 24px;
    }

    .twism-row {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
    }

    .twism-how-to {
      &-title {
        margin-bottom: 16px;

        img {
          width: 32px;
          height: 32px;
          border: 1px solid $color-grat-botticelli;
          margin-right: 16px;
          border-radius: 50%;
        }
        :global(h3) {
          font-size: 16px;
        }
      }

      &-icon {
        min-width: 32px;
        margin-right: 16px;
      }
    }

    .twism-share {
      margin-top: 40px;

      &-items {
        margin-top: 16px;
        width: 100%;
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;
      }

      &-item {
        display: flex;
        flex-flow: column nowrap;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        outline: 0;

        &-copy {
          min-width: 54px;
        }

        &.twism-copied {
          .twism-share-icon {
            background-color: #e1f9f3;
          }
        }
      }

      &-icon {
        & > div {
          max-height: 24px;
          max-width: 24px;
        }
      }

      &-icon {
        width: 40px;
        height: 40px;
        border-radius: 7px;
        background-color: var(--twism-theme-primary600);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 8px;
        transition: background-color 0.3s ease-in-out;
        position: relative;

        > div {
          position: absolute;
        }
      }
    }
  }
</style>
